import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { Calendar as CalendarIcon } from "lucide-react";
import ClickAwayListener from "@mui/material/ClickAwayListener";

import { cn } from "@/utils/css";
import { TranslatedText } from "@/components/common/text";
import { CalendarPanel } from "@/components/common/inputs";
import { useQuery } from "@/utils/query";
import { setDeliveryDate, setDeliveryDateEnd, setPickupDate, setPickupDateEnd } from "@/state/searchInputSlice";
import { today } from "@/utils/date";
import { setCurrentSearch } from "@/state/currentSearchSlice";
import { useLocation } from "react-router";

export const CalendarInput = ({ name, isLastInput = false, className, label, disabled = false }) => {
    const dispatch = useDispatch();
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const {
        pickupDate: queryPickupDate,
        pickupDateEnd: queryPickupDateEnd,
        deliveryDate: queryDeliveryDate,
        deliveryDateEnd: queryDeliveryDateEnd,
    } = useQuery();
    const { pickupDate, pickupDateEnd, deliveryDate, deliveryDateEnd, searchType } = useSelector((state) => state.searchInput);
    const isPickup = name === "pickup";
    const isLoadboard = searchType === "loadboard";
    const isLoadFinder = searchType === "load_finder";
    const supportsRange = isLoadboard || isLoadFinder;
    const allowsAny = isLoadboard; // Only loadboard allows "Any" (no date required)
    const date = isPickup ? pickupDate : deliveryDate;
    const dateEnd = isPickup ? pickupDateEnd : deliveryDateEnd;
    const location = useLocation();

    const isToday = date === today;
    const isRange = dateEnd && date !== dateEnd;

    const formatDateRange = (startDate, endDate) => {
        const start = dayjs(startDate);
        const end = dayjs(endDate);

        // Check if same month and year
        if (start.month() === end.month() && start.year() === end.year()) {
            // Same month: "Jan 11 - 18 '25"
            return `${start.format("MMM D")} - ${end.format("D")} '${end.format("YY")}`;
        } else {
            // Different months: "Jan 11 - Feb 18 '25"
            return `${start.format("MMM D")} - ${end.format("MMM D")} '${end.format("YY")}`;
        }
    };

    const handleDateChange = (date, dateEnd = null) => {
        if (isPickup) {
            dispatch(setPickupDate(date));
            dispatch(setPickupDateEnd(dateEnd));
        } else {
            dispatch(setDeliveryDate(date));
            dispatch(setDeliveryDateEnd(dateEnd));
        }
    };

    const handleCalendarOpen = () => {
        setIsCalendarOpen(!isCalendarOpen);
    };

    const handleCalendarDates = (value) => {
        // Handle both single date and date range
        if (Array.isArray(value) && supportsRange) {
            // Date range mode (for loadboard and load_finder). The first click is committed
            // right away as a single day, so closing the calendar keeps it; the second click
            // widens it into a range and closes.
            const [startDate, endDate] = value;
            const startFormatted = dayjs(startDate).format("YYYY-MM-DD");
            const endFormatted = endDate ? dayjs(endDate).format("YYYY-MM-DD") : null;

            handleDateChange(startFormatted, endFormatted);
            if (endFormatted) {
                setIsCalendarOpen(false);
            }
        } else if (supportsRange) {
            // First click in range mode - commit the day, keep the calendar open for a range
            handleDateChange(dayjs(value).format("YYYY-MM-DD"), null);
        } else {
            // Single date mode (for non-loadboard searches)
            const selectedDate = Array.isArray(value) ? value[0] : value;
            handleDateChange(dayjs(selectedDate).format("YYYY-MM-DD"), null);
            setIsCalendarOpen(false);
        }
    };

    useEffect(() => {
        if (allowsAny) {
            handleDateChange(null);
        } else if (isPickup) {
            handleDateChange(today);
        }
    }, [searchType]);

    useEffect(() => {
        const queryDate = isPickup ? queryPickupDate : queryDeliveryDate;
        const queryDateEnd = isPickup ? queryPickupDateEnd : queryDeliveryDateEnd;

        if (!queryDate && allowsAny) {
            dispatch(
                setCurrentSearch(
                    isPickup
                        ? {
                              pickupDate: null,
                              pickupDateEnd: null,
                          }
                        : {
                              deliveryDate: null,
                              deliveryDateEnd: null,
                          },
                ),
            );
        } else {
            const dateToSet =
                queryDate && dayjs(queryDate).format("YYYY-MM-DD") ? dayjs(queryDate).format("YYYY-MM-DD") : isPickup ? today : null;
            const dateEndToSet = queryDateEnd && dayjs(queryDateEnd).format("YYYY-MM-DD") ? dayjs(queryDateEnd).format("YYYY-MM-DD") : null;
            dispatch(
                setCurrentSearch(
                    isPickup
                        ? {
                              pickupDate: dateToSet,
                              pickupDateEnd: dateEndToSet,
                          }
                        : {
                              deliveryDate: dateToSet,
                              deliveryDateEnd: dateEndToSet,
                          },
                ),
            );
            setTimeout(() => {
                //Needed to use setTimeout to avoid being set to null by changing searchType to loadboard
                handleDateChange(dateToSet, dateEndToSet);
            }, 0);
        }
        // Clearing done via SearchInputs clear
    }, [queryPickupDate, queryPickupDateEnd, queryDeliveryDate, queryDeliveryDateEnd, location.pathname]);

    return (
        <div className={cn("relative", className)}>
            <button
                className={cn(
                    "w-full h-[64px] md:w-[200px] inline-flex flex-col items-start justify-center bg-cm-p-surface-container px-4 py-[10px] mb-4 md:mb-0 rounded-b-lg md:rounded-bl-none md:rounded-r-lg",
                    {
                        "md:rounded-br-none": isCalendarOpen,
                        "md:mr-1 md:rounded-r-none": !isLastInput,
                        "cursor-not-allowed": disabled,
                    },
                )}
                type="button"
                onClick={handleCalendarOpen}
                disabled={disabled}
            >
                <TranslatedText className="text-sm text-cm-p-text-secondary mb-[2px]">
                    {label ?? `${isPickup ? "Pickup" : "Delivery"} date`}
                </TranslatedText>
                <span className="inline-flex flex-row justify-between w-full items-center">
                    <span
                        className={cn("font-medium text-base", {
                            "text-cm-p-text-secondary": disabled,
                        })}
                    >
                        {!date ? (
                            <TranslatedText>Any</TranslatedText>
                        ) : isRange ? (
                            formatDateRange(date, dateEnd)
                        ) : !isToday ? (
                            dayjs(date).format("MMM D 'YY")
                        ) : (
                            <TranslatedText>Today</TranslatedText>
                        )}
                    </span>
                    <span>
                        <CalendarIcon className="w-4 h-4 text-cm-p-text-secondary" />
                    </span>
                </span>
            </button>
            {isCalendarOpen && (
                <div
                    className={cn(
                        "absolute top-[69px] right-[-77px] bg-cm-p-surface w-[354px] rounded-xl z-dropdown border border-cm-p-outline shadow-[0px_4px_14px_-3px_rgba(93,93,93,0.08)]",
                        {
                            "right-[-73px]": !isLastInput,
                        },
                    )}
                >
                    <ClickAwayListener onClickAway={() => setIsCalendarOpen(false)}>
                        <div>
                            <CalendarPanel
                                onChange={handleCalendarDates}
                                value={
                                    date && dateEnd && supportsRange
                                        ? [dayjs(date).toDate(), dayjs(dateEnd).toDate()]
                                        : date
                                          ? dayjs(date).toDate()
                                          : undefined
                                }
                                maxDate={isPickup ? dayjs().add(7, "day").toDate() : undefined}
                                selectRange={supportsRange}
                                onAny={
                                    allowsAny
                                        ? () => {
                                              handleDateChange(null, null);
                                              setIsCalendarOpen(false);
                                          }
                                        : undefined
                                }
                                onToday={() => {
                                    handleDateChange(today, null);
                                    setIsCalendarOpen(false);
                                }}
                                onApply={() => setIsCalendarOpen(false)}
                            />
                        </div>
                    </ClickAwayListener>
                </div>
            )}
        </div>
    );
};
